import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Reveal } from "../Reveal";
import { SCHEDULE_DAYS } from "../../config/site";

const accent = (label) => {
  const l = (label || "").toLowerCase();
  if (!l) return "transparent";
  if (l.includes("kids")) return "#7FA86A";
  if (l.includes("feminino")) return "#B48EAD";
  if (l.includes("boxe") || l.includes("muay")) return "#8A8A8A";
  if (l.includes("preparação") || l.includes("flexibilidade")) return "#C9A24B";
  if (l.includes("nogi")) return "#5C5C5C";
  return "#B8862E";
};

const Cell = ({ label }) =>
  label ? (
    <span className="flex items-start gap-2">
      <span className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: accent(label) }} />
      <span className="font-body text-ink text-sm leading-snug">{label}</span>
    </span>
  ) : (
    <span className="font-body text-slate/25 text-sm">—</span>
  );

export const Horarios = () => {
  const [i, setI] = useState(0);
  const day = SCHEDULE_DAYS[i];
  const go = (d) => setI((p) => (p + d + SCHEDULE_DAYS.length) % SCHEDULE_DAYS.length);

  return (
    <section id="horarios" data-testid="schedule-section" className="relative py-24 md:py-32 bg-paper">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">Horários</span>
          <h2 className="font-display text-ink uppercase tracking-tight leading-[0.95] text-4xl md:text-5xl lg:text-6xl mt-5">Grade de horários</h2>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 flex items-center justify-between gap-4">
            <button onClick={() => go(-1)} data-testid="schedule-prev" aria-label="Dia anterior" className="w-12 h-12 shrink-0 rounded-full border border-line flex items-center justify-center text-ink hover:border-gold hover:text-gold transition-colors">
              <CaretLeft size={20} weight="bold" />
            </button>
            <div className="flex-1 flex items-center justify-center gap-2">
              {SCHEDULE_DAYS.map((d, idx) => (
                <button key={d.day} onClick={() => setI(idx)} aria-label={d.day} className={`h-2.5 rounded-full transition-all duration-300 ${idx === i ? "w-8 bg-gold" : "w-2.5 bg-line hover:bg-gold/40"}`} />
              ))}
            </div>
            <button onClick={() => go(1)} data-testid="schedule-next" aria-label="Próximo dia" className="w-12 h-12 shrink-0 rounded-full border border-line flex items-center justify-center text-ink hover:border-gold hover:text-gold transition-colors">
              <CaretRight size={20} weight="bold" />
            </button>
          </div>
        </Reveal>

        <div className="mt-8 min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.div key={day.day} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
              <h3 className="font-display text-gold uppercase tracking-[0.14em] text-2xl md:text-3xl text-center mb-6">{day.day}</h3>

              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-2xl border border-line overflow-hidden">
                <div className="grid grid-cols-12 bg-ink text-cream font-body text-xs tracking-[0.14em] uppercase">
                  <div className="col-span-3 px-5 py-4">Horário</div>
                  <div className="col-span-3 px-5 py-4">Tatame 1 · grande</div>
                  <div className="col-span-3 px-5 py-4">Tatame 2 · menor</div>
                  <div className="col-span-3 px-5 py-4">Tatame 3 · menor</div>
                </div>
                <div className="divide-y divide-line">
                  {day.rows.map((r, ri) => (
                    <div key={ri} className="grid grid-cols-12 items-center px-0 hover:bg-sand transition-colors">
                      <div className="col-span-3 px-5 py-4 font-display text-gold text-lg tracking-wide">{r.time}</div>
                      <div className="col-span-3 px-5 py-4"><Cell label={r.t1} /></div>
                      <div className="col-span-3 px-5 py-4"><Cell label={r.t2} /></div>
                      <div className="col-span-3 px-5 py-4"><Cell label={r.t3} /></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile: blocos por horário */}
              <div className="md:hidden flex flex-col gap-3">
                {day.rows.map((r, ri) => (
                  <div key={ri} className="rounded-xl border border-line p-4">
                    <div className="font-display text-gold text-lg tracking-wide">{r.time}</div>
                    <div className="mt-3 flex flex-col gap-2">
                      {[["Tatame 1", r.t1], ["Tatame 2", r.t2], ["Tatame 3", r.t3]].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex items-start gap-2">
                          <span className="font-body text-[11px] uppercase tracking-wider text-slate/50 w-16 shrink-0 pt-0.5">{k}</span>
                          <Cell label={v} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
